// Push Notification Manager for PWA
// Handles subscription, permission requests, and notification management

class PushNotificationManager {
    constructor() {
        this.subscription = null;
        this.vapidPublicKey = null;
        this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    }

    /**
     * Check if push notifications are supported
     */
    isNotificationSupported() {
        return this.isSupported;
    }

    /**
     * Get current notification permission status
     */
    getPermissionStatus() {
        if (!this.isSupported) return 'unsupported';
        return Notification.permission;
    }

    /**
     * Request notification permission from user
     */
    async requestPermission() {
        if (!this.isSupported) {
            throw new Error('Push notifications are not supported');
        }

        const permission = await Notification.requestPermission();
        return permission;
    }

    /**
     * Get VAPID public key from backend
     */
    async getVapidPublicKey() {
        if (this.vapidPublicKey) {
            return this.vapidPublicKey;
        }

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(`${backendUrl}/push-vapid-key`);
        
        if (!response.ok) {
            throw new Error('Failed to get VAPID public key');
        }

        const data = await response.json();
        this.vapidPublicKey = data.publicKey;
        return this.vapidPublicKey;
    }

    /**
     * Convert base64 VAPID key to Uint8Array
     */
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    /**
     * Subscribe to push notifications
     */
    async subscribe() {
        console.log('🔔 [PushManager] subscribe() called');
        
        if (!this.isSupported) {
            throw new Error('Push notifications are not supported');
        }

        // Check permission
        let permission = Notification.permission;
        console.log('🔔 [PushManager] Current permission:', permission);
        
        if (permission === 'default') {
            console.log('🔔 [PushManager] Requesting permission...');
            permission = await this.requestPermission();
            console.log('🔔 [PushManager] Permission granted:', permission);
        }

        if (permission !== 'granted') {
            throw new Error('Notification permission denied');
        }

        // Check if service worker is registered
        console.log('🔔 [PushManager] Checking service worker registration...');
        const existingRegistration = await navigator.serviceWorker.getRegistration();
        
        if (!existingRegistration) {
            console.log('🔔 [PushManager] No service worker registered, registering now...');
            // Register service worker if not already registered
            try {
                const registration = await navigator.serviceWorker.register('/service-worker.js');
                console.log('🔔 [PushManager] Service worker registered:', registration);
                // Wait for it to be active
                await navigator.serviceWorker.ready;
                console.log('🔔 [PushManager] Service worker ready');
            } catch (error) {
                console.error('🔔 [PushManager] Service worker registration failed:', error);
                throw new Error('Service worker registration failed: ' + error.message);
            }
        } else {
            console.log('🔔 [PushManager] Service worker already registered, state:', existingRegistration.active?.state);
        }

        // Get service worker registration
        console.log('🔔 [PushManager] Waiting for service worker to be ready...');
        const registration = await navigator.serviceWorker.ready;
        console.log('🔔 [PushManager] Service worker ready, subscribing to push...');

        // Get VAPID public key
        const vapidPublicKey = await this.getVapidPublicKey();
        console.log('🔔 [PushManager] Got VAPID key:', vapidPublicKey.substring(0, 20) + '...');
        const convertedVapidKey = this.urlBase64ToUint8Array(vapidPublicKey);

        // Subscribe to push notifications
        console.log('🔔 [PushManager] Subscribing to push manager...');
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedVapidKey
        });

        console.log('🔔 [PushManager] Push subscription created');
        this.subscription = subscription;

        // Send subscription to backend
        console.log('🔔 [PushManager] Sending subscription to backend...');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        let token = sessionStorage.getItem('token');
        
        // Parse token if it's stored as JSON string
        if (token) {
            try {
                token = JSON.parse(token);
                console.log('🔔 [PushManager] Token parsed from JSON');
            } catch (e) {
                console.log('🔔 [PushManager] Token is already a plain string');
            }
        }
        
        console.log('🔔 [PushManager] Token available:', !!token);

        const deviceInfo = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            browser: this.getBrowserInfo()
        };

        const response = await fetch(`${backendUrl}/push-subscribe`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-access-token': token
            },
            body: JSON.stringify({
                subscription: subscription.toJSON(),
                deviceInfo
            })
        });

        console.log('🔔 [PushManager] Backend response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('🔔 [PushManager] Backend error:', errorData);
            throw new Error('Failed to save subscription to backend: ' + (errorData.message || response.statusText));
        }

        const result = await response.json();
        console.log('🔔 [PushManager] Subscription saved successfully:', result);
        
        // Store subscription info in localStorage
        localStorage.setItem('push-subscribed', 'true');
        localStorage.setItem('push-subscription-id', result.subscriptionId);

        return subscription;
    }

    /**
     * Unsubscribe from push notifications
     */
    async unsubscribe() {
        if (!this.subscription) {
            const registration = await navigator.serviceWorker.ready;
            this.subscription = await registration.pushManager.getSubscription();
        }

        if (!this.subscription) {
            return false;
        }

        // Unsubscribe from browser
        await this.subscription.unsubscribe();

        // Notify backend
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const token = sessionStorage.getItem('token');

        try {
            await fetch(`${backendUrl}/push-unsubscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token
                },
                body: JSON.stringify({
                    endpoint: this.subscription.endpoint
                })
            });
        } catch (error) {
            console.error('Failed to notify backend of unsubscribe:', error);
        }

        this.subscription = null;
        localStorage.removeItem('push-subscribed');
        localStorage.removeItem('push-subscription-id');

        return true;
    }

    /**
     * Check if user is currently subscribed
     */
    async isSubscribed() {
        console.log('🔔 [PushManager] isSubscribed called, isSupported:', this.isSupported);
        
        if (!this.isSupported) {
            console.log('🔔 [PushManager] Not supported, returning false');
            return false;
        }

        try {
            console.log('🔔 [PushManager] Getting service worker registration...');
            
            // Check if service worker is registered at all
            const registration = await navigator.serviceWorker.getRegistration();
            if (!registration) {
                console.log('🔔 [PushManager] No service worker registered yet');
                return false;
            }
            
            console.log('🔔 [PushManager] Service worker registration found, state:', registration.active?.state);
            
            // Wait for service worker to be ready with a timeout
            const readyPromise = navigator.serviceWorker.ready;
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Service worker ready timeout')), 5000)
            );
            
            const readyRegistration = await Promise.race([readyPromise, timeoutPromise]);
            console.log('🔔 [PushManager] Service worker ready, getting subscription...');
            
            const subscription = await readyRegistration.pushManager.getSubscription();
            console.log('🔔 [PushManager] Subscription:', subscription ? 'EXISTS' : 'NULL');
            this.subscription = subscription;
            return subscription !== null;
        } catch (error) {
            console.error('🔔 [PushManager] Error checking subscription status:', error);
            return false;
        }
    }

    /**
     * Get browser information
     */
    getBrowserInfo() {
        const ua = navigator.userAgent;
        let browser = 'Unknown';

        if (ua.indexOf('Firefox') > -1) {
            browser = 'Firefox';
        } else if (ua.indexOf('Chrome') > -1) {
            browser = 'Chrome';
        } else if (ua.indexOf('Safari') > -1) {
            browser = 'Safari';
        } else if (ua.indexOf('Edge') > -1) {
            browser = 'Edge';
        }

        return browser;
    }

    /**
     * Test notification - show a local notification
     */
    async showTestNotification() {
        if (!this.isSupported || Notification.permission !== 'granted') {
            throw new Error('Notifications not available');
        }

        // Try server-side notification first (will trigger badge update)
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
            const token = sessionStorage.getItem('token');
            
            if (token) {
                let parsedToken = token;
                try {
                    parsedToken = JSON.parse(token);
                } catch (e) {
                    // Token is already plain string
                }

                const response = await fetch(`${backendUrl}/push-test-notification`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-access-token': parsedToken
                    }
                });

                const result = await response.json();
                if (result.success) {
                    console.log('Server test notification sent:', result);
                    return;
                }
            }
        } catch (error) {
            console.warn('Server notification failed, falling back to local:', error);
        }

        // Fallback to local notification
        const registration = await navigator.serviceWorker.ready;
        
        await registration.showNotification('PPE Scope Test', {
            body: 'Push notifications are working!',
            icon: '/logo192.png',
            badge: '/badge-72x72.png',
            tag: 'test-notification',
            requireInteraction: false
        });
    }
}

// Export singleton instance
const pushNotificationManager = new PushNotificationManager();
export default pushNotificationManager;
